/**
 * This is largely copied from the PageOption component in the context ribbon
 * /public/js/designer-flux/components/TopBar/ContextRibbon/PageOption.tsx
 *
 * Work to refactor these into a shared component will be done in
 * https://webflow.atlassian.net/browse/DEVPL-2785
 *
 * Please see the ticket for more information
 */

import * as React from 'react';
import {
  SearchableListItem,
  SearchableListItemBody,
  SearchableListItemActions,
  SearchableListItemAdornment,
  Tooltip,
  HomeIcon,
  TooltipProps,
  HStack,
  Text,
  ChevronLargeRightIcon,
  MoreIcon,
  View,
  OptionType,
} from '@packages/systems/spring';
import {isHomePage, isFolder} from '@packages/shared/utils/PageUtils';
import {
  nodeAncestry,
  getPageNodes,
  getPage,
} from '@packages/shared/utils/PageTreeUtils';
import {PageRecordType} from '@packages/systems/core/records/PageRecord';
import {selectPageState} from '@packages/systems/designer/page';
import {useDesignerSelector} from '@packages/systems/designer/flux';

const PathSegment = ({children}: {children: React.ReactNode}) => (
  <Text
    colorVariant="secondary"
    whiteSpace="nowrap"
    textOverflow="ellipsis"
    overflow="hidden"
  >
    {children}
  </Text>
);

const StyledMoreIcon = React.forwardRef<HTMLDivElement>((_, ref) => (
  <View
    ref={ref}
    borderRadius="$1"
    backgroundColor="$uiCardBackground"
    width={16}
    height={12}
    display="flex"
    flexDirection="column"
    justifyContent="center"
  >
    <MoreIcon tabIndex={-1} size={16} colorVariant="secondary" />
  </View>
));

const PagePath = ({path}: {path: string[]}) => (
  <HStack spacing={1} maxWidth={480}>
    {/* if the path is less than or equal to 4, render the full path */}
    {path.length <= 4 ? (
      path.map((segment, index) => (
        <React.Fragment key={index}>
          {index > 0 && <ChevronLargeRightIcon colorVariant="secondary" />}
          <PathSegment>{segment}</PathSegment>
        </React.Fragment>
      ))
    ) : (
      /* otherwise, render the first segment, a more icon, and the last 3 segments */
      <>
        <PathSegment>{path[0]}</PathSegment>
        <ChevronLargeRightIcon colorVariant="secondary" />
        <StyledMoreIcon />
        {path.slice(-3).map((segment, index) => (
          <React.Fragment key={index}>
            <ChevronLargeRightIcon colorVariant="secondary" />
            <PathSegment>{segment}</PathSegment>
          </React.Fragment>
        ))}
      </>
    )}
  </HStack>
);

const FilePathContent = ({path}: {path: string[]}) => (
  <HStack spacing={0} marginLeft={4}>
    {path.length > 1 && (
      <>
        <StyledMoreIcon />
        <ChevronLargeRightIcon />
      </>
    )}
    <Text textOverflow="ellipsis" overflow="hidden" marginRight={2}>
      {path[path.length - 1]}
    </Text>
  </HStack>
);

const FilePathTooltip = ({
  path,
  isDisabled,
}: {
  path: string[];
  isDisabled: TooltipProps['isDisabled'];
}) => {
  if (path.length > 1) {
    return (
      <Tooltip isDisabled={isDisabled} label={<PagePath path={path} />}>
        <FilePathContent path={path} />
      </Tooltip>
    );
  }
  return <FilePathContent path={path} />;
};

const getPageAutomationId = (label: string) =>
  `${label
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[\[\]]/g, '')}-page`;

const getPagePath = (ancestry: Immutable.List<PageRecordType>) =>
  ancestry
    // Remove the root node and current page from our ancestry.
    .slice(1, -1)
    // Ancestry is ordered from leaf -> root, but the folders is root -> leaf.
    .reverse()
    // Filter out any non-folder nodes.
    .filter((node: PageRecordType) => isFolder(node))
    .map((node: PageRecordType) => node.get('name'))
    .toArray();

type PageOptionProps = {
  option: OptionType;
};

export const PageSelectOption = ({option}: PageOptionProps) => {
  const pageState = useDesignerSelector(selectPageState);
  const page = getPage(option.value, pageState);
  const pages = getPageNodes(pageState);
  const ancestry = nodeAncestry(option.value, pages);
  const path = getPagePath(ancestry);
  return (
    <SearchableListItem
      prefix={option.prefix}
      data-automation-id={getPageAutomationId(option.label)}
    >
      <SearchableListItemBody
        description={
          path.length > 0 && <FilePathTooltip path={path} isDisabled={false} />
        }
      >
        {option.label}
      </SearchableListItemBody>
      <SearchableListItemActions>
        {isHomePage(page) && (
          <SearchableListItemAdornment isInteractive>
            <Tooltip asChild content="This is the current homepage">
              <HomeIcon
                density="comfortable"
                tabIndex={-1}
                colorVariant="secondary"
              />
            </Tooltip>
          </SearchableListItemAdornment>
        )}
      </SearchableListItemActions>
    </SearchableListItem>
  );
};

export default PageSelectOption;
